import * as React from 'react'
import { I18nService } from '../../../../services/utils/i18n.service'
import { CastingDayId, castingSlotAllowedPatternFirstDate } from '../../../../services/models/castings.service'
import { CastingStore, StoreProps, castingStoreGetMinDuration, castingStoreIsComplexDurationDifferentThanRangeAllowed } from '../../../../services/stores/casting.store'
import { formatTime, parseTime, parseTimeRange } from '../../../../services/utils/utils.service'
import { moment } from '../../../../services/utils/moment.service'

type Slot = StoreProps['data']['slots'][0]

type Params = StoreProps & {
  getComplexTimes: (dayId: CastingDayId, formattedStartDate?: string, isStart?: boolean, startedAtHour?: number) => number[]
}

const i18nScoped = new I18nService(require('./casting-complex-multi-slots.hook.yml'))

export function useCastingComplexMultiSlots ({ data, data: { slots, castingType }, store, getComplexTimes }: Params) {
  const getSlotFirstDateInstance = React.useCallback((slot: Slot) => castingSlotAllowedPatternFirstDate(slot.allowed_patterns[0].day_id, slot.start_date), [])

  const setSlotsPreservingFrequency = React.useCallback((slots: CastingStore['data']['slots']) => {
    if (!slots.length) slots = [{ duration: null, weeks_interval: data.slots[0]?.weeks_interval ?? null, start_date: null, end_date: null, allowed_patterns: [] }]
    store.setSlots(slots)
  }, [data.slots, store])

  const buildSlotForDayId = React.useCallback((dayId: CastingDayId, formattedStartDate = slots[0].start_date): Slot | null => {
    if (formattedStartDate > slots[0].end_date) {
      window.alert(i18nScoped.t('date_after_end_date'))
      return null
    }

    const newSlot: Slot = {
      start_date: formattedStartDate,
      end_date: slots[0].end_date,
      weeks_interval: slots[0].weeks_interval,
      duration: null,
      allowed_patterns: null
    }

    const fromHour = getComplexTimes(dayId, formattedStartDate)[0]
    const untilHours = getComplexTimes(dayId, formattedStartDate, false, fromHour)
    const minSlotDuration = castingStoreGetMinDuration(data)

    if (castingStoreIsComplexDurationDifferentThanRangeAllowed(data)) {
      const untilHour = untilHours.find(_ => _ - fromHour >= minSlotDuration / 3600) || untilHours[untilHours.length - 1]
      newSlot.duration = minSlotDuration
      newSlot.allowed_patterns = [{ day_id: dayId, from_hour: formatTime(fromHour), until_hour: formatTime(untilHour), additional_days: null }]
    } else {
      newSlot.duration = (untilHours[0] - fromHour) * 3600
      newSlot.allowed_patterns = [{ day_id: dayId, from_hour: formatTime(fromHour), until_hour: formatTime(untilHours[0]), additional_days: null }]
    }

    return newSlot
  }, [data, getComplexTimes, slots])

  const onComplexOneshotDayToggle = React.useCallback((day: Date) => {
    // for complex oneshot, we suppose each slot has a single allowed pattern
    const notEmptySlots = slots.filter(_ =>  _.allowed_patterns.length === 1)
    const newSlots: typeof slots = JSON.parse(JSON.stringify(notEmptySlots.filter(_ => !getSlotFirstDateInstance(_).isSame(day, 'day'))))

    if (!notEmptySlots.find(_ => getSlotFirstDateInstance(_).isSame(day, 'day'))) {
      const slot = buildSlotForDayId(day.getDay(), moment(day).format('YYYY-MM-DD'))
      if (slot) newSlots.push(slot)
    }

    setSlotsPreservingFrequency(newSlots)
  }, [buildSlotForDayId, getSlotFirstDateInstance, setSlotsPreservingFrequency, slots])

  const onComplexReccurringDayIdToggle = React.useCallback((dayId: CastingDayId) => {
    const notEmptySlots = slots.filter(_ =>  _.allowed_patterns.length === 1)
    const newSlots = notEmptySlots.filter(slot => slot.allowed_patterns[0].day_id !== dayId)

    if (notEmptySlots.length === newSlots.length) {
      const slot = buildSlotForDayId(dayId)
      if (slot) newSlots.push(slot)
    }

    setSlotsPreservingFrequency(newSlots)
  }, [buildSlotForDayId, setSlotsPreservingFrequency, slots])

  const addPicker = React.useCallback((dayId: CastingDayId, formattedStartDate: string) => {
    const slot = buildSlotForDayId(dayId, formattedStartDate)
    if (slot) store.setSlots(slots.concat(slot))
  }, [buildSlotForDayId, slots, store])

  const setComplexSlotField = React.useCallback((oldSlot: Slot, changes : { field: 'from_hour' | 'until_hour', value: string } | { field: 'duration', value: number }) => {
    const newSlot = { ...oldSlot, allowed_patterns: [{ ...oldSlot.allowed_patterns[0] }] }
    if (changes.field === 'duration') newSlot.duration = changes.value
    else newSlot.allowed_patterns[0][changes.field] = changes.value

    // ensure until hour is still valid after chaning from_hour
    if (changes.field === 'from_hour') {
      const validUntilChoices = getComplexTimes(oldSlot.allowed_patterns[0].day_id, oldSlot.start_date, false, parseTime(changes.value))
      const preferedUntilHour = parseTime(changes.value) - parseTime(oldSlot.allowed_patterns[0].from_hour) + parseTime(newSlot.allowed_patterns[0].until_hour)
      newSlot.allowed_patterns[0].until_hour = formatTime(validUntilChoices.find(_ => _ === preferedUntilHour) ? preferedUntilHour : validUntilChoices[0])
    }

    if (!castingStoreIsComplexDurationDifferentThanRangeAllowed(data)) {
      const [parsedFrom, parsedTo] = parseTimeRange(newSlot.allowed_patterns[0].from_hour, newSlot.allowed_patterns[0].until_hour)
      newSlot.duration = (parsedTo - parsedFrom) * 3600
    }

    store.setSlots(slots.map(_ => _ === oldSlot ? newSlot : _))
  }, [data, getComplexTimes, slots, store])

  const slotsByDays = React.useMemo(() => {
    const slotsByDays: Slot[][] = []

    for (const slot of slots) {
      if (slot.allowed_patterns.length !== 1) continue
      let existing = slotsByDays.find(_ => getSlotFirstDateInstance(_[0]).isSame(getSlotFirstDateInstance(slot), 'day'))
      if (!existing) slotsByDays.push(existing = [])
      existing.push(slot)
    }

    return slotsByDays.sort(([a], [b]) => {
      if (castingType === 'casting_recurring') return (a.allowed_patterns[0].day_id || 7) - (b.allowed_patterns[0].day_id || 7)
      return getSlotFirstDateInstance(a).valueOf() - getSlotFirstDateInstance(b).valueOf()
    })
  }, [castingType, getSlotFirstDateInstance, slots])

  const tableDataByDays = React.useMemo(() => {
    return slotsByDays.map(slotsByDay => {
      return slotsByDay.map(slot => {
        return {
          startDate: getSlotFirstDateInstance(slot),
          fromHour: slot.allowed_patterns[0].from_hour,
          untilHour: slot.allowed_patterns[0].until_hour,
          dayId: slot.allowed_patterns[0].day_id,
          slot,
          setDuration: (duration: number) => setComplexSlotField(slot, { field: 'duration', value: duration }),
          setFromHour: (fromHour: string) => setComplexSlotField(slot, { field: 'from_hour', value: fromHour }),
          setUntilHour: (untilHour: string) => setComplexSlotField(slot, { field: 'until_hour', value: untilHour }),
          remove: () => setSlotsPreservingFrequency(slots.filter(_ => _ !== slot)),
          setAllowedPattern: (allowedPattern: Slot['allowed_patterns'][0]) => {
            store.setSlots(slots.map(_ => _ === slot ? { ..._, allowed_patterns: [allowedPattern] } : _))
          }
        }
      })
    })
  }, [getSlotFirstDateInstance, setComplexSlotField, setSlotsPreservingFrequency, slots, slotsByDays, store])

  return { tableDataByDays, onComplexOneshotDayToggle, onComplexReccurringDayIdToggle, addPicker }
}
